import { Link } from '@tanstack/react-router';
import type { ReviewItem } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { clientDateText, reviewFlag, waitingFor } from '../../lib/review-display';
import { TASK_TYPE_META } from '../../lib/task-display';
import { versionBadge } from '../../lib/version-display';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { Chip } from '../ui/chip';
import { Skeleton } from '../ui/skeleton';

/** The columns of Figma A05.5 "Table": Draft, Type, Submitted by, Waiting, Client date, and the Review button. */
const GRID =
  'grid items-center gap-(--mbm-space-3) px-(--mbm-space-6) grid-cols-[minmax(0,1fr)_auto] lg:gap-0 lg:grid-cols-[minmax(0,486fr)_minmax(0,136fr)_minmax(0,186fr)_minmax(0,106fr)_minmax(0,166fr)_minmax(0,72fr)]';
const HEAD = 'text-label-small text-text-tertiary uppercase';

export function ReviewRowsHeader() {
  return (
    <div className={cx(GRID, 'hidden h-(--mbm-size-review-head) border-b border-border-line lg:grid')}>
      <span className={HEAD}>Draft</span>
      <span className={HEAD}>Type</span>
      <span className={HEAD}>Submitted by</span>
      <span className={HEAD}>Waiting</span>
      <span className={HEAD}>Client date</span>
      <span aria-hidden />
    </div>
  );
}

const REVIEW_BUTTON =
  'inline-flex h-(--mbm-size-button-small) items-center justify-center justify-self-end rounded-full bg-action-primary px-(--mbm-space-4) text-label-medium text-text-on-dark focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

/** A draft waiting on the lead's QA, or one they sent back. */
export function ReviewRow({ item }: { item: ReviewItem }) {
  const type = TASK_TYPE_META[item.taskType];
  const flag = reviewFlag(item);
  const waiting = waitingFor(item.submittedAt);
  return (
    <div
      className={cx(
        GRID,
        'min-h-(--mbm-size-review-row) border-b border-border-line py-(--mbm-space-3) last:border-b-0',
      )}
    >
      <div className="flex min-w-0 items-center gap-(--mbm-space-3)">
        <span
          className={
            item.fix
              ? 'rounded-(--mbm-radius-xs) border border-border-default px-(--mbm-space-2) py-(--mbm-space-0-5) text-label-small text-text-secondary'
              : 'rounded-(--mbm-radius-xs) bg-action-primary px-(--mbm-space-2) py-(--mbm-space-0-5) text-label-small text-text-on-dark'
          }
        >
          {item.fix ? 'fix' : versionBadge(item.versionNumber)}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-(--mbm-space-2)">
            <span className="truncate text-label-large text-text-strong">{item.title}</span>
            {flag ? <Chip className="shrink-0">{flag}</Chip> : null}
          </span>
          <span className="truncate text-body-xsmall text-text-secondary">
            {[item.businessName, item.projectName].filter(Boolean).join(' · ')}
          </span>
        </div>
      </div>
      <span className="hidden items-center gap-(--mbm-space-2) text-body-small text-text-primary lg:flex">
        <Icon icon={type.icon} size="sm" />
        {type.label}
      </span>
      <span className="hidden items-center gap-(--mbm-space-2) text-body-small text-text-primary lg:flex">
        <Avatar tone="neutral" size="xxs" initials={initialsOf(item.submittedByName)} />
        {item.submittedByName ?? '—'}
      </span>
      <span
        className={cx(
          'hidden text-label-medium lg:block',
          waiting.late ? 'text-feedback-warning' : 'text-text-primary',
        )}
      >
        {waiting.text}
      </span>
      <span className="hidden text-body-small text-text-primary lg:block">{clientDateText(item)}</span>
      {/* A fix is answered on the task page, beside the version it changes; a version has its own review page. */}
      {item.fix ? (
        <Link
          to="/subdomain/admin/tasks/$taskId"
          params={{ taskId: item.taskId }}
          aria-label={`Review ${item.title}`}
          className={REVIEW_BUTTON}
        >
          Review
        </Link>
      ) : (
        <Link
          to="/subdomain/admin/workbench/reviews/$taskId"
          params={{ taskId: item.taskId }}
          aria-label={`Review ${item.title}`}
          className={REVIEW_BUTTON}
        >
          Review
        </Link>
      )}
    </div>
  );
}

export function ReviewRowSkeleton() {
  return (
    <div className={cx(GRID, 'min-h-(--mbm-size-review-row) border-b border-border-line py-(--mbm-space-3)')}>
      <Skeleton className="h-(--mbm-space-9) w-2/3" />
      <Skeleton className="hidden h-(--mbm-space-4) w-3/4 lg:block" />
      <Skeleton className="hidden h-(--mbm-space-4) w-3/4 lg:block" />
      <Skeleton className="hidden h-(--mbm-space-4) w-1/2 lg:block" />
      <Skeleton className="hidden h-(--mbm-space-4) w-3/4 lg:block" />
      <Skeleton className="h-(--mbm-size-button-small) w-(--mbm-space-9) justify-self-end" />
    </div>
  );
}
